import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Statistic, Table, Tag, message } from 'antd';

interface ShortUrlStatsModalProps {
  /** 是否显示弹框 */
  open: boolean;
  /** 关闭弹框的回调 */
  onCancel: () => void;
  /** 短网址ID */
  shortId: string;
}

interface StatsData {
  totalPv: number;
  uniqueIpCount: number;
  todayPv: number;
  provinceStats: Record<string, number>;
  browserStats: Record<string, number>;
  osStats: Record<string, number>;
  deviceTypeStats: Record<string, number>;
  recentLogs: Array<{
    ip: string;
    province: string;
    city: string;
    referer: string;
    accessTime: string;
    browserName?: string;
    browserVersion?: string;
    osName?: string;
    deviceType?: string;
  }>;
}

/**
 * 短网址统计数据展示组件
 * 
 * @author dongdong.fdd
 * @date 2025/01/XX
 */
const ShortUrlStatsModal: React.FC<ShortUrlStatsModalProps> = ({
  open,
  onCancel,
  shortId,
}) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsData | null>(null);

  // 获取统计数据
  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/u/stats/${shortId}`);
      const json = await response.json();
      
      if (json.success && json.data) {
        setStats(json.data);
      } else {
        message.error(json.message || '获取统计数据失败');
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error('获取统计数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [shortId]);

  useEffect(() => {
    if (open && shortId) {
      fetchStats();
    }
  }, [open, shortId, fetchStats]);

  // 表格列定义
  const columns = [
    {
      title: '访问时间',
      dataIndex: 'accessTime',
      key: 'accessTime',
      width: 100,
      render: (time: string) => {
        try {
          const date = new Date(time);
          const now = new Date();
          const diff = now.getTime() - date.getTime();
          const seconds = Math.floor(diff / 1000);
          const minutes = Math.floor(seconds / 60);
          const hours = Math.floor(minutes / 60);
          const days = Math.floor(hours / 24);
          
          if (days > 0) return `${days}天前`;
          if (hours > 0) return `${hours}小时前`;
          if (minutes > 0) return `${minutes}分钟前`;
          return `${seconds}秒前`;
        } catch {
          return time;
        }
      },
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '省份',
      dataIndex: 'province',
      key: 'province',
      width: 80,
      render: (province: string) => <Tag>{province}</Tag>,
    },
    {
      title: '城市',
      dataIndex: 'city',
      key: 'city',
      width: 80,
    },
    {
      title: '浏览器',
      key: 'browser',
      width: 150,
      render: (_: any, record: any) => {
        const browser = record.browserName || '未知';
        const version = record.browserVersion ? ` ${record.browserVersion}` : '';
        return `${browser}${version}`;
      },
    },
    {
      title: '操作系统',
      dataIndex: 'osName',
      key: 'osName',
      width: 120,
    },
    {
      title: '设备',
      dataIndex: 'deviceType',
      key: 'deviceType',
      width: 70,
      render: (type: string) => {
        const color = type === 'Mobile' ? 'blue' : 'green';
        return <Tag color={color}>{type || 'PC'}</Tag>;
      },
    },
    {
      title: '来源',
      dataIndex: 'referer',
      key: 'referer',
      render: (referer: string) => referer || '-',
      ellipsis: true,
    },
  ];

  // 省份分布数据（转换为表格数据）
  const provinceData = stats?.provinceStats
    ? Object.entries(stats.provinceStats)
        .map(([province, count]) => ({
          province,
          count,
          percentage: stats.totalPv > 0 
            ? (((count as number) / stats.totalPv) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => (b.count as number) - (a.count as number))
    : [];

  // 浏览器分布数据
  const browserData = stats?.browserStats
    ? Object.entries(stats.browserStats)
        .map(([browser, count]) => ({
          browser,
          count,
          percentage: stats.totalPv > 0 
            ? (((count as number) / stats.totalPv) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => (b.count as number) - (a.count as number))
    : [];

  // 操作系统分布数据
  const osData = stats?.osStats
    ? Object.entries(stats.osStats)
        .map(([os, count]) => ({
          os,
          count,
          percentage: stats.totalPv > 0 
            ? (((count as number) / stats.totalPv) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => (b.count as number) - (a.count as number))
    : [];

  // 设备类型分布数据
  const deviceTypeData = stats?.deviceTypeStats
    ? Object.entries(stats.deviceTypeStats)
        .map(([deviceType, count]) => ({
          deviceType,
          count,
          percentage: stats.totalPv > 0 
            ? (((count as number) / stats.totalPv) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => (b.count as number) - (a.count as number))
    : [];

  return (
    <Modal
      title="访问统计"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
      ) : stats ? (
        <div>
          {/* 统计卡片 */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
            <Statistic
              title="总访问量"
              value={stats.totalPv}
              suffix="次"
            />
            <Statistic
              title="独立IP"
              value={stats.uniqueIpCount}
              suffix="个"
            />
            <Statistic
              title="今日访问"
              value={stats.todayPv}
              suffix="次"
            />
          </div>

          {/* 省份分布 */}
          <div style={{ marginBottom: '24px' }}>
            <h3>省份分布</h3>
            <Table
              dataSource={provinceData}
              columns={[
                {
                  title: '省份',
                  dataIndex: 'province',
                  key: 'province',
                },
                {
                  title: '访问量',
                  dataIndex: 'count',
                  key: 'count',
                },
                {
                  title: '占比',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (percentage: string) => `${percentage}%`,
                },
              ]}
              pagination={false}
              size="small"
              rowKey="province"
            />
          </div>

          {/* 浏览器分布 */}
          <div style={{ marginBottom: '24px' }}>
            <h3>浏览器分布</h3>
            <Table
              dataSource={browserData}
              columns={[
                {
                  title: '浏览器',
                  dataIndex: 'browser',
                  key: 'browser',
                },
                {
                  title: '访问量',
                  dataIndex: 'count',
                  key: 'count',
                },
                {
                  title: '占比',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (percentage: string) => `${percentage}%`,
                },
              ]}
              pagination={false}
              size="small"
              rowKey="browser"
            />
          </div>

          {/* 操作系统分布 */}
          <div style={{ marginBottom: '24px' }}>
            <h3>操作系统分布</h3>
            <Table
              dataSource={osData}
              columns={[
                {
                  title: '操作系统',
                  dataIndex: 'os',
                  key: 'os',
                },
                {
                  title: '访问量',
                  dataIndex: 'count',
                  key: 'count',
                },
                {
                  title: '占比',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (percentage: string) => `${percentage}%`,
                },
              ]}
              pagination={false}
              size="small"
              rowKey="os"
            />
          </div>

          {/* 设备类型分布 */}
          <div style={{ marginBottom: '24px' }}>
            <h3>设备类型分布</h3>
            <Table
              dataSource={deviceTypeData}
              columns={[
                {
                  title: '设备类型',
                  dataIndex: 'deviceType',
                  key: 'deviceType',
                  render: (type: string) => {
                    const color = type === 'Mobile' ? 'blue' : 'green';
                    return <Tag color={color}>{type}</Tag>;
                  },
                },
                {
                  title: '访问量',
                  dataIndex: 'count',
                  key: 'count',
                },
                {
                  title: '占比',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (percentage: string) => `${percentage}%`,
                },
              ]}
              pagination={false}
              size="small"
              rowKey="deviceType"
            />
          </div>

          {/* 最近访问记录 */}
          <div>
            <h3>最近访问记录</h3>
            <Table
              dataSource={stats.recentLogs}
              columns={columns}
              pagination={{ pageSize: 10 }}
              size="small"
              scroll={{ x: 1000 }}
              rowKey={(record, index) => `${record.ip}-${record.accessTime}-${index}`}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>暂无数据</div>
      )}
    </Modal>
  );
};

export default ShortUrlStatsModal;

